import { z } from "zod"

import {
  branchIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const stripeTransactionIdSchema = z.string().regex(/^txn_[A-Za-z0-9_]+$/)
const stripeSourceIdSchema = z.string().trim().min(1).max(255)
const currencySchema = z.string().regex(/^[A-Z]{3}$/)
const exceptionCodeSchema = z.string().regex(/^[a-z][a-z0-9_]{2,63}$/)

export const stripeAccountingReconciliationRequestV1Schema = z.object({
  reconciliationId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  createdFrom: z.string().datetime({ offset: false }),
  createdTo: z.string().datetime({ offset: false }),
  observedAt: z.string().datetime({ offset: false }),
  limit: z.number().int().min(1).max(100),
  startingAfter: stripeTransactionIdSchema.nullable(),
}).strict().superRefine((value, context) => {
  const createdFrom = Date.parse(value.createdFrom)
  const createdTo = Date.parse(value.createdTo)
  if (createdTo < createdFrom) {
    context.addIssue({
      code: "custom",
      message: "createdTo must not precede createdFrom.",
      path: ["createdTo"],
    })
  }
  if (createdTo - createdFrom > 31 * 24 * 60 * 60 * 1_000) {
    context.addIssue({
      code: "custom",
      message: "Reconciliation windows are limited to 31 days.",
      path: ["createdTo"],
    })
  }
})

export const stripeAccountingReconciliationItemV1Schema = z.object({
  observationId: entityIdSchema,
  providerBalanceTransactionId: stripeTransactionIdSchema,
  sourceId: stripeSourceIdSchema.nullable(),
  category: z.enum(["charge", "refund", "dispute", "payout", "adjustment"]),
  amountMinor: z.number().int(),
  feeMinor: z.number().int().nonnegative(),
  netMinor: z.number().int(),
  currency: currencySchema,
  providerStatus: z.enum(["pending", "available"]),
  providerCreatedAt: z.number().int().nonnegative(),
  availableOn: z.number().int().nonnegative(),
  localRecordType: z.enum([
    "invoice_payment_attempt",
    "customer_deposit",
    "payment_correction",
    "bank_transaction",
  ]).nullable(),
  localRecordId: z.string().trim().min(1).max(255).nullable(),
  paymentEntry: z.string().trim().min(1).max(255).nullable(),
  bankTransaction: z.string().trim().min(1).max(255).nullable(),
  state: z.enum(["matched", "review_required"]),
  exceptionCodes: z.array(exceptionCodeSchema).max(16)
    .refine((items) => new Set(items).size === items.length),
}).strict().superRefine((value, context) => {
  if (value.amountMinor - value.feeMinor !== value.netMinor) {
    context.addIssue({
      code: "custom",
      message: "Stripe net must equal amount less fee.",
      path: ["netMinor"],
    })
  }
  if (
    (value.state === "matched") !== (value.exceptionCodes.length === 0)
  ) {
    context.addIssue({
      code: "custom",
      message: "Matched items cannot retain reconciliation exceptions.",
      path: ["exceptionCodes"],
    })
  }
})

export const stripeAccountingReconciliationResultV1Schema = z.object({
  entityType: z.literal("stripe_accounting_reconciliation"),
  entityId: entityIdSchema,
  state: z.enum(["matched", "review_required"]),
  version: z.literal(1),
  legalEntityId: legalEntityIdSchema,
  provider: z.literal("stripe"),
  providerAccountId: z.string().regex(/^acct_[A-Za-z0-9_]+$/),
  transactionCount: z.number().int().min(0).max(100),
  matchedCount: z.number().int().min(0).max(100),
  reviewRequiredCount: z.number().int().min(0).max(100),
  items: z.array(stripeAccountingReconciliationItemV1Schema).max(100),
  nextCursor: stripeTransactionIdSchema.nullable(),
}).strict().superRefine((value, context) => {
  if (
    value.transactionCount !== value.items.length ||
    value.transactionCount !== value.matchedCount + value.reviewRequiredCount
  ) {
    context.addIssue({
      code: "custom",
      message: "Reconciliation counts must agree.",
      path: ["transactionCount"],
    })
  }
  if (
    (value.state === "matched") !== (value.reviewRequiredCount === 0)
  ) {
    context.addIssue({
      code: "custom",
      message: "Run state must reflect its exceptions.",
      path: ["state"],
    })
  }
  if (
    new Set(value.items.map((item) => item.providerBalanceTransactionId)).size !==
    value.items.length
  ) {
    context.addIssue({
      code: "custom",
      message: "A page cannot repeat a provider balance transaction.",
      path: ["items"],
    })
  }
})

export type StripeAccountingReconciliationRequestV1 = z.infer<
  typeof stripeAccountingReconciliationRequestV1Schema
>
export type StripeAccountingReconciliationItemV1 = z.infer<
  typeof stripeAccountingReconciliationItemV1Schema
>
export type StripeAccountingReconciliationResultV1 = z.infer<
  typeof stripeAccountingReconciliationResultV1Schema
>
